import { useI18n } from '@affine/i18n';
import { useLiveData, useService } from '@toeverything/infra';
import { useEffect, useRef } from 'react';

import { TutorialPersistence } from '../../services/tutorial-persistence';
import { TutorialRegistry } from '../../services/tutorial-registry';
import type { TutorialSession } from '../../types';

export const CANVAS_BLOCK_EDITING_SESSION_ID = 'tutorial:canvas-block-editing';

const EDGELESS_NOTE_TAG = 'affine-edgeless-note';
const EDGELESS_NOTE_CONTAINER_SELECTOR =
  '[data-testid="edgeless-note-container"]';

function findEdgelessNote(event: Event): HTMLElement | null {
  return (
    event
      .composedPath()
      .find(
        (target): target is HTMLElement =>
          target instanceof HTMLElement &&
          target.localName === EDGELESS_NOTE_TAG
      ) ?? null
  );
}

/**
 * The note's visible box. The note host keeps its unscaled layout size; the
 * container carries the note's canvas scale, so only its rect matches what the
 * user sees.
 */
export function canvasBlockEditingHighlightTarget(
  note: HTMLElement
): HTMLElement | null {
  return note.querySelector<HTMLElement>(EDGELESS_NOTE_CONTAINER_SELECTOR);
}

function noteIsEditing(note: HTMLElement): boolean {
  return (
    canvasBlockEditingHighlightTarget(note)?.getAttribute('data-editing') ===
    'true'
  );
}

function eventTargetsEditableContent(event: KeyboardEvent): boolean {
  return event.composedPath().some(target => {
    if (!(target instanceof HTMLElement)) return false;
    return (
      target.isContentEditable ||
      target instanceof HTMLInputElement ||
      target instanceof HTMLTextAreaElement ||
      target instanceof HTMLSelectElement
    );
  });
}

function createCanvasBlockEditingSession(
  t: ReturnType<typeof useI18n>,
  targetRef: { current: HTMLElement | null }
): TutorialSession {
  return {
    id: CANVAS_BLOCK_EDITING_SESSION_ID,
    context: {
      category: 'basics',
      targetAudience: 'all',
    },
    steps: [
      {
        animation: 'highlight',
        blockCanvasInteraction: true,
        centerOnTarget: true,
        comparison: [
          {
            description:
              t[
                'com.streamlain.streamlain.tutorial.canvas-block-editing.single-click.description'
              ](),
            title:
              t[
                'com.streamlain.streamlain.tutorial.canvas-block-editing.single-click.title'
              ](),
            scene: 'note-select',
          },
          {
            description:
              t[
                'com.streamlain.streamlain.tutorial.canvas-block-editing.double-click.description'
              ](),
            title:
              t[
                'com.streamlain.streamlain.tutorial.canvas-block-editing.double-click.title'
              ](),
            scene: 'note-edit',
          },
        ],
        content:
          t[
            'com.streamlain.streamlain.tutorial.canvas-block-editing.content'
          ](),
        position: 'auto',
        surface: 'disclosure',
        targetResolver: () =>
          targetRef.current
            ? canvasBlockEditingHighlightTarget(targetRef.current)
            : null,
        title:
          t['com.streamlain.streamlain.tutorial.canvas-block-editing.title'](),
        trigger: { type: 'manual' },
      },
    ],
  };
}

export function useCanvasBlockEditingCoachmark(): void {
  const registry = useService(TutorialRegistry);
  const persistence = useService(TutorialPersistence);
  const tutorialProgress = useLiveData(persistence.states$);
  const targetRef = useRef<HTMLElement | null>(null);
  const t = useI18n();

  useEffect(() => {
    const session = createCanvasBlockEditingSession(t, targetRef);
    registry.register(session);
    return () => {
      if (
        registry.activeTutorial$.value?.session.id ===
        CANVAS_BLOCK_EDITING_SESSION_ID
      ) {
        // A workspace/language unmount is not an explicit user dismissal and
        // must not persist the coachmark as completed.
        registry.dismiss();
      }
      registry.unregister(session.id);
    };
  }, [registry, t]);

  useEffect(() => {
    if (
      tutorialProgress.states[CANVAS_BLOCK_EDITING_SESSION_ID] === 'completed'
    ) {
      return;
    }

    // Only the user's own clicks teach: the assistant's clicks in the app are not trusted
    // events, and a coachmark opened by them would block the assistant's next step.
    const handleClick = (event: MouseEvent) => {
      if (
        event.button !== 0 ||
        !event.isTrusted ||
        registry.activeTutorial$.value
      )
        return;
      const note = findEdgelessNote(event);
      if (!note || noteIsEditing(note)) return;
      targetRef.current = note;
      registry.start(CANVAS_BLOCK_EDITING_SESSION_ID);
    };

    const handleDoubleClick = (event: MouseEvent) => {
      if (
        !event.isTrusted ||
        registry.activeTutorial$.value?.session.id !==
          CANVAS_BLOCK_EDITING_SESSION_ID
      ) {
        return;
      }
      const note = findEdgelessNote(event);
      if (note !== targetRef.current) return;
      queueMicrotask(() => registry.complete());
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (
        registry.activeTutorial$.value?.session.id !==
          CANVAS_BLOCK_EDITING_SESSION_ID ||
        (event.key !== 'Backspace' && event.key !== 'Delete') ||
        !targetRef.current ||
        noteIsEditing(targetRef.current) ||
        eventTargetsEditableContent(event)
      ) {
        return;
      }
      event.preventDefault();
      event.stopImmediatePropagation();
    };

    document.addEventListener('click', handleClick, true);
    document.addEventListener('dblclick', handleDoubleClick, true);
    document.addEventListener('keydown', handleKeyDown, true);
    return () => {
      document.removeEventListener('click', handleClick, true);
      document.removeEventListener('dblclick', handleDoubleClick, true);
      document.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [registry, tutorialProgress]);
}
